<template>
  <div class="research-group-card shadow-sm" :class="cardClassesRef">
    <button type="button" v-if="props.selectMode" class="overlay" @click="emit('selected')"></button>
    <router-link v-if="dataGroupRef && !props.selectMode" class="overlay" :to="dataGroupRef.pageUrl"></router-link>
    <div class="card-content">
      <div class="card-image">
        <img :src="logoRef">
      </div>
      <div class="card-body">
        <div class="card-title-row">
          <div class="card-title">
            {{ dataGroupRef?.name }}
          </div>
          <div v-if="statusTextRef" class="badge card-status" :class="statusStyleRef">
            {{ statusTextRef }}
          </div>
        </div>
        <div class="card-subtitle">
          {{ workspaceRef?.workspaceTypeInfo?.description }} managed by <UserLink :userId="dataGroupRef?.ownerId" class="link-dark" dimmed></UserLink>
        </div>
        <div v-if="!props.small" class="card-details">
          {{ lastSyncDateTextRef }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.research-group-card {
  position: relative;
  max-width: 500px;
  border: 1px solid #f4f4f4;

  &.selected {
    border-color: #eee;
    background: #f4f4f4;

    .card-image {
      background: #f4f4f4;
    }
  }

  .card-content {
    display: flex;
    flex-direction: row;
    gap: 0.25rem;
  }

  .card-image {
    padding: 12px 6px;
    width: 62px;
    background-color: #f1f1f1;

    img {
      width: 48px;
      object-fit: contain;
    }
  }

  .card-body {
    padding: 0.25em;
    line-height: 1.3;
  }

  .card-title-row {
    display: grid;
    grid-template-columns: auto max-content;

    .card-title {
      margin-bottom: 0.1rem;
    }

    .card-status {
      margin-bottom: 3px;
      background-color: #888;
      padding: 2px 0.5rem;
      color: white;

      &.info {
        background-color: var(--bs-info);
      }

      &.success {
        background-color: var(--bs-success);
      }

      &.secondary {
        background-color: #bbb;
      }

      &.warning {
        background-color: var(--bs-warning);
        color: black;
      }
    }
  }

  .card-subtitle {
    margin-bottom: 1rem;
    font-size: 0.75rem;
    color: #888;
  }

  .card-details {
    font-size: 0.75rem;
    color: #888;

    svg {
      margin-right: 0.25rem;
    }
  }
}

.card-small {
  max-width: 400px;

  .card-image {
    padding: 0.5em;
    width: 50px;

    img {
      width: 35px;
    }
  }

  .card-subtitle {
    margin-bottom: 0;
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useSyncStore } from '@/rd/SyncStore'
import { workspaceTypeInfos } from '@/rd/ResearchDataModel'
import { LoadMode } from '@/util/AsyncData'
import { DateTimeUtil } from '@/util/LuxonUtil'
import UserLink from '@/manage/users/UserLink.vue'
import { useTokenManager } from '@/auth/TokenManager'
import { StatusInfo } from '@/util/AppUtil'

const props = defineProps<{
  dataGroupId: string
  small?: boolean
  selectMode?: boolean
  selectedId?: string
  status?: StatusInfo
}>()

const emit = defineEmits([
  "selected",
])

const dataGroupStore = useDataGroupStore()
const workspaceStore = useWorkspaceStore()
const syncStore = useSyncStore()
const tokenManager = useTokenManager()

const memberStatusText = {
  owner: 'Owner',
  member: undefined,
}
const memberStatusStyle = {
  owner: 'warning',
  member: undefined,
}

const dataGroupRef = computed(() => dataGroupStore.getAsyncGroup(props.dataGroupId, LoadMode.EnsureLoaded)?.data)
const workspaceRef = computed(() => workspaceStore.getWorkspacesForGroups([dataGroupRef.value?.id], LoadMode.EnsureLoaded).at(0))
const logoRef = computed(() => workspaceTypeInfos.find(t => t.id == (workspaceRef.value?.dataFileBaseType ?? 'TestData'))?.icon)
const lastSyncRef = computed(() => syncStore.getAsyncSync(workspaceRef.value?.lastSyncId)) // loaded with workspace
const lastSyncDateTextRef = computed(() => {
  const relativeDate = lastSyncRef.value?.data?.createdDate
  return relativeDate ? `Last updated ${DateTimeUtil.toRelativePastDate(relativeDate)}` : "Never synced"
})
const cardClassesRef = computed(() => ({ 
  'card-small': props.small, 
  selected: props.selectedId == dataGroupRef.value?.id
}))

const memberStatusRef = computed(() => dataGroupRef.value?.ownerId == tokenManager.current.userId ? 'owner' : 'member')

const statusTextRef = computed(() => props.status?.text ?? memberStatusText[memberStatusRef.value])
const statusStyleRef = computed(() => props.status?.style ?? memberStatusStyle[memberStatusRef.value])

</script>
